import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, PenLine, Star } from "lucide-react";
import api from "@/lib/api";
import { ReviewDialog } from "@/components/ReviewDialog";

const Stars = ({ n }) => {
  if (!n) return null;
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={12} className="fill-[var(--mirror-accent)] text-[var(--mirror-accent)]" />
      ))}
    </span>
  );
};

const ReviewCard = ({ r, mine }) => (
  <motion.figure
    data-testid={`review-card-${r.id}`}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7 }}
    className={`rounded-2xl border p-7 flex flex-col ${
      mine
        ? "border-[rgba(140,122,107,0.4)] bg-[rgba(140,122,107,0.06)]"
        : "border-[rgba(242,236,228,0.08)]"
    }`}
  >
    {mine && (
      <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--mirror-accent)] mb-3">
        Your reflection
      </span>
    )}
    <blockquote className="font-serif-display text-xl font-light italic leading-relaxed text-[var(--mirror-text)]">
      “{r.content}”
    </blockquote>
    <figcaption className="mt-6 flex items-center justify-between gap-3">
      <div>
        {r.anonymous ? (
          <>
            <span className="block text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
              Before Mirror
            </span>
            <span className="text-[var(--mirror-accent)] text-sm mt-1 block">
              {r.archetype}
            </span>
          </>
        ) : (
          <span className="text-[var(--mirror-text-2)] text-sm">— {r.display_name}</span>
        )}
      </div>
      <Stars n={r.rating} />
    </figcaption>
  </motion.figure>
);

export default function Reviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState(null);
  const [mine, setMine] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => {
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
    api.get("/reviews/mine").then(({ data }) => setMine(data && data.id ? data : null)).catch(() => {});
  };

  useEffect(load, []);

  return (
    <div className="min-h-screen px-6 sm:px-10 py-8">
      <header className="max-w-4xl mx-auto flex items-center justify-between">
        <button
          data-testid="reviews-back-button"
          onClick={() => navigate("/begin")}
          className="flex items-center gap-2 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-sm"
        >
          <ArrowLeft size={16} /> Begin
        </button>
        <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
          Reflections
        </span>
      </header>

      <main className="max-w-4xl mx-auto mt-20">
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight"
        >
          What people say of the mirror.
        </motion.h1>
        <p className="mt-4 text-[var(--mirror-text-3)] text-sm max-w-md">
          Honest words from people who sat with Mirror. Some named, some known only by who
          they were before.
        </p>

        <button
          data-testid="reviews-share-button"
          onClick={() => setDialogOpen(true)}
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-[rgba(242,236,228,0.15)] px-6 py-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-2)] hover:text-[var(--mirror-text)] hover:border-[rgba(242,236,228,0.3)] transition-colors duration-500"
        >
          <PenLine size={14} /> {mine ? "Edit your reflection" : "Share your reflection"}
        </button>

        {reviews === null && (
          <p className="mt-16 text-[var(--mirror-text-3)] text-sm">Gathering…</p>
        )}

        {reviews && reviews.length === 0 && (
          <div data-testid="reviews-empty-state" className="mt-20 text-center">
            <p className="font-serif-display text-2xl font-light text-[var(--mirror-text-2)]">
              No reflections shared yet.
            </p>
            <p className="mt-3 text-sm text-[var(--mirror-text-3)]">
              Be the first to leave one.
            </p>
          </div>
        )}

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {reviews?.map((r) => (
            <ReviewCard key={r.id} r={r} mine={mine && r.id === mine.id} />
          ))}
        </div>
      </main>

      <ReviewDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmitted={load}
      />
    </div>
  );
}
